// One touchpoint through a tenant's eyes: the tenant's own versions, plus a
// synthetic "Inherited" row for what serves when none of them is active — the
// platform's live version, or the code default. Activating a tenant version
// overrides the ladder for this tenant only; "revert to inherited" is
// activation with null. Preview is the platform endpoint — rendering a draft
// with example variables is tenant-independent.
import { useEffect, useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import { Link, useParams } from "react-router";
import type { LlmParams, LlmTouchpointSlug, PromptVersion } from "@gb/admin-contracts";
import { EmptyState } from "@/components/gb/empty-state";
import { PageHeader } from "@/components/gb/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { usePreview } from "./prompts-api";
import { ScopeBadge } from "./tenant-prompts-page";
import {
  useActivateTenantVersion,
  useCreateTenantVersion,
  useTenantTouchpoint,
  useTenantVersions,
} from "./tenant-prompts-api";

export function TenantPromptEditorPage() {
  const { tenantId = "", slug = "" } = useParams();
  const touchpoint = useTenantTouchpoint(tenantId, slug as LlmTouchpointSlug);
  const versions = useTenantVersions(tenantId, slug as LlmTouchpointSlug);
  const create = useCreateTenantVersion(tenantId, slug as LlmTouchpointSlug);
  const activate = useActivateTenantVersion(tenantId, slug as LlmTouchpointSlug);
  const preview = usePreview(slug as LlmTouchpointSlug);

  const [body, setBody] = useState("");
  const [model, setModel] = useState("");
  const [effort, setEffort] = useState("");
  const [maxTokens, setMaxTokens] = useState("");
  const [temperature, setTemperature] = useState("");
  const [note, setNote] = useState("");
  const [seeded, setSeeded] = useState(false);

  // Seed the editor from what this tenant actually resolves to, once it arrives.
  useEffect(() => {
    if (seeded || !touchpoint.data) return;
    const t = touchpoint.data;
    const live = t.tenantActive ?? t.platformActive ?? {
      body: t.codeDefault.body,
      params: t.codeDefault.params,
    };
    setBody(live.body);
    setModel(live.params.model ?? "");
    setEffort(live.params.effort ?? "");
    setMaxTokens(live.params.maxTokens?.toString() ?? "");
    setTemperature(live.params.temperature?.toString() ?? "");
    setSeeded(true);
  }, [seeded, touchpoint.data]);

  if (touchpoint.isError) return <EmptyState icon={SlidersHorizontal} title="Unknown touchpoint" />;
  if (!touchpoint.data) return null;
  const t = touchpoint.data;
  const tenantRows = versions.data ?? [];

  // What serves when no tenant version is active — shown as a version row so
  // "the ladder" is visible where activation happens.
  // A version switched on but written from an older code prompt does not run.
  const platformLive = t.platformActive && !t.platformActive.outOfDate ? t.platformActive : null;
  const inherited: PromptVersion = platformLive ?? {
    id: null,
    touchpointSlug: t.slug,
    version: 0,
    body: t.codeDefault.body,
    params: t.codeDefault.params,
    isActive: false,
    note: "The code default, shipped with this build.",
    createdBy: "code",
    createdAt: null,
    outOfDate: false,
  };
  const inheritedLive = t.tenantActive === null || t.tenantActive.outOfDate;
  const inheritedLabel = platformLive ? `platform v${platformLive.version}` : "code v0";

  const loadIntoEditor = (version: Pick<PromptVersion, "body" | "params">) => {
    setBody(version.body);
    setModel(version.params.model ?? "");
    setEffort(version.params.effort ?? "");
    setMaxTokens(version.params.maxTokens?.toString() ?? "");
    setTemperature(version.params.temperature?.toString() ?? "");
  };

  const params: LlmParams = {
    ...(model.trim() ? { model: model.trim() } : {}),
    ...(effort.trim() ? { effort: effort.trim() as LlmParams["effort"] } : {}),
    ...(maxTokens.trim() ? { maxTokens: Number(maxTokens) } : {}),
    ...(temperature.trim() ? { temperature: Number(temperature) } : {}),
  };

  return (
    <div>
      <div className="mb-1 text-[0.75rem]">
        <Link to={`/tenants/${tenantId}/prompts`} className="text-brand hover:underline">
          ← Tenant prompts
        </Link>
      </div>
      <PageHeader
        eyebrow={t.area}
        title={t.name}
        actions={<ScopeBadge scope={t.effectiveScope} />}
      />
      <p className="mt-1 max-w-[720px] text-[0.8rem] text-muted">{t.description}</p>
      <div className="mt-1 font-mono text-[0.72rem] text-faint">
        {t.slug} · {t.provider} · serving{" "}
        {t.tenantActiveVersion !== null ? `tenant v${t.tenantActiveVersion}` : inheritedLabel} ·
        effective model {t.effectiveModel}
      </div>

      <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_320px]">
        <div>
          <label className="text-[0.72rem] font-semibold tracking-wide text-muted uppercase">
            System prompt body
          </label>
          {Object.keys(t.variables).length > 0 && (
            <p className="mt-1 text-[0.72rem] text-faint">
              Placeholders this touchpoint supplies:{" "}
              {Object.entries(t.variables).map(([name, v]) => (
                <span key={name} className="mr-2 font-mono">
                  {`{{${name}}}`}
                  {v.required ? "*" : ""}
                </span>
              ))}
            </p>
          )}
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="mt-2 min-h-[360px] font-mono text-[0.78rem] leading-relaxed"
          />

          <div className="mt-3 rounded-md border border-border-inner bg-canvas p-3">
            <div className="text-[0.68rem] font-semibold tracking-wide text-muted uppercase">
              Guard (code-owned, always appended)
            </div>
            <p className="mt-1 font-mono text-[0.72rem] leading-relaxed whitespace-pre-wrap text-faint">
              {t.guard || "(none)"}
            </p>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button
              onClick={() =>
                void create
                  .mutateAsync({ body, params, note: note.trim() || undefined })
                  .catch(() => undefined)
              }
              disabled={create.isPending || !body.trim()}
            >
              Save as new tenant version
            </Button>
            <Button
              variant="outline"
              onClick={() => void preview.mutateAsync({ body, params }).catch(() => undefined)}
              disabled={preview.isPending || !body.trim()}
            >
              Preview render
            </Button>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why this version exists…"
              className="max-w-[320px]"
            />
          </div>
          {create.isError && (
            <p className="mt-2 text-[0.78rem] text-danger">{String(create.error)}</p>
          )}
          {preview.data && (
            <div className="mt-4 rounded-md border border-border bg-white p-3">
              <div className="text-[0.68rem] font-semibold tracking-wide text-muted uppercase">
                Rendered with example variables
              </div>
              {preview.data.issues.length > 0 && (
                <ul className="mt-1 list-disc pl-5 text-[0.75rem] text-danger">
                  {preview.data.issues.map((issue) => (
                    <li key={issue}>{issue}</li>
                  ))}
                </ul>
              )}
              <pre className="mt-2 overflow-x-auto font-mono text-[0.72rem] leading-relaxed whitespace-pre-wrap text-ink-prose">
                {preview.data.rendered}
              </pre>
            </div>
          )}
        </div>

        <div>
          <div className="text-[0.72rem] font-semibold tracking-wide text-muted uppercase">
            Params for the next version
          </div>
          <div className="mt-2 space-y-2">
            <Input value={model} onChange={(e) => setModel(e.target.value)} placeholder={`model (default: ${t.effectiveModel})`} />
            <Input value={effort} onChange={(e) => setEffort(e.target.value)} placeholder="effort: low | medium | high | xhigh | max" />
            <Input value={maxTokens} onChange={(e) => setMaxTokens(e.target.value)} placeholder="maxTokens" inputMode="numeric" />
            {t.capabilities.temperature && (
              <Input value={temperature} onChange={(e) => setTemperature(e.target.value)} placeholder="temperature (0–1)" inputMode="decimal" />
            )}
          </div>

          <div className="mt-6 text-[0.72rem] font-semibold tracking-wide text-muted uppercase">
            Tenant versions
          </div>
          <div className="mt-2 space-y-2">
            {tenantRows.map((version) => (
              <TenantVersionRow
                key={version.id ?? "code"}
                version={version}
                label={`v${version.version}`}
                live={version.isActive && !version.outOfDate}
                activateLabel="Activate"
                onActivate={() => void activate.mutateAsync(version.id).catch(() => undefined)}
                onLoad={() => loadIntoEditor(version)}
                busy={activate.isPending}
              />
            ))}
            <TenantVersionRow
              version={inherited}
              label={`Inherited (${inheritedLabel})`}
              live={inheritedLive}
              activateLabel="Revert to inherited"
              onActivate={() => void activate.mutateAsync(null).catch(() => undefined)}
              onLoad={() => loadIntoEditor(inherited)}
              busy={activate.isPending}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function TenantVersionRow({
  version,
  label,
  live,
  activateLabel,
  onActivate,
  onLoad,
  busy,
}: {
  version: PromptVersion;
  label: string;
  live: boolean;
  activateLabel: string;
  onActivate: () => void;
  onLoad: () => void;
  busy: boolean;
}) {
  return (
    <div
      className={cn(
        "rounded-md border p-3",
        live ? "border-brand bg-brand-tint-3" : "border-border-inner bg-white",
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[0.75rem] font-semibold text-ink">{label}</span>
        {live ? (
          <span className="font-display text-[0.6rem] font-semibold tracking-[0.08em] text-brand uppercase">
            Live
          </span>
        ) : version.outOfDate ? (
          <span className="font-display text-[0.6rem] font-semibold tracking-[0.08em] text-warn uppercase">
            Out of date
          </span>
        ) : (
          <Button size="sm" variant="outline" onClick={onActivate} disabled={busy}>
            {activateLabel}
          </Button>
        )}
      </div>
      <div className="mt-1 text-[0.7rem] text-faint">
        {version.createdBy}
        {version.createdAt ? ` · ${new Date(version.createdAt).toLocaleString()}` : ""}
      </div>
      {version.outOfDate && (
        <div className="mt-1 text-[0.72rem] text-warn">
          Written from an older code prompt, so it is not used. Load it into the editor and save it
          again to use it.
        </div>
      )}
      {version.note && <div className="mt-1 text-[0.72rem] text-muted">{version.note}</div>}
      {Object.keys(version.params).length > 0 && (
        <div className="mt-1 font-mono text-[0.68rem] text-faint">
          {JSON.stringify(version.params)}
        </div>
      )}
      <button
        type="button"
        onClick={onLoad}
        className="mt-1 text-[0.7rem] font-semibold text-brand hover:underline"
      >
        Load into editor
      </button>
    </div>
  );
}
